import { t } from 'ttag'
import { useState, useCallback, useEffect } from 'react'
import { useMutation } from 'react-query'
import PropTypes from 'prop-types'

import { api, errors as apiErrors } from '@h2/api'
import { useForm } from '@h2/common/components/hooks/useForm'
import { Text } from '@h2/components/typography/text'
import { Alert } from '@h2/components/display/alert'
import { Field } from '@h2/components/inputs/field'
import { TextInput } from '@h2/components/inputs/text'
import { Button } from '@h2/components/buttons/button'
import { Flex } from '@h2/components/layout/flex'

const RESEND_COOLDOWN_SECONDS = 60
const Form = ({ name, token, onVerified, onCancel }) => {
  const [alert, setAlert] = useState(null)
  const [currentToken, setCurrentToken] = useState(token)
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS)

  useEffect(() => {
    if (cooldown <= 0) {
      return
    }

    const timer = setInterval(() => {
      setCooldown(prev => (prev > 0 ? prev - 1 : 0))
    }, 1000)

    return () => clearInterval(timer)
  }, [cooldown])

  const { register, handleOnSubmit, error, isInvalid } = useForm({
    code(form) {
      if (!form.code?.trim()) {
        return t`Verification code is required`
      }

      if (!/^\d{6}$/.test(form.code.trim())) {
        return t`Code must be 6 digits`
      }
    }
  })
  const { mutate: verify, isLoading: isVerifying } = useMutation(
    form =>
      api.fetch('/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ name, token: currentToken, code: form.code.trim() })
      }),
    {
      onSuccess: data => {
        if (data.user) {
          onVerified(data)
        } else if (data.mfaPending) {
          setCurrentToken(data.mfaPending.token)
          setAlert({ type: 'error', message: t`Verification failed. Please try again.` })
        }
      },
      onError: err => {
        const message = apiErrors.getMessage(err)

        setAlert({ type: 'error', message: message || t`Verification failed. Please try again.` })
      }
    }
  )
  const { mutate: resend, isLoading: isResending } = useMutation(
    () =>
      api.fetch('/auth/mfa/challenge', {
        method: 'POST',
        body: JSON.stringify({ name, token: currentToken })
      }),
    {
      onSuccess: data => {
        if (data.mfaPending) {
          setCurrentToken(data.mfaPending.token)
          setCooldown(RESEND_COOLDOWN_SECONDS)
          setAlert({ type: 'success', message: t`A new code has been sent to your email.` })
        }
      },
      onError: err => {
        const message = apiErrors.getMessage(err)

        setAlert({ type: 'error', message: message || t`Could not resend code. Please try again later.` })
      }
    }
  )
  const onResend = useCallback(() => {
    setAlert(null)
    resend()
  }, [resend])

  return (
    <form onSubmit={handleOnSubmit(form => verify(form))}>
      <Text margin="0 0 20px 0">
        {t`A verification code has been sent to your email address. Please enter it below to complete sign-in.`}
      </Text>
      {alert && (
        <Alert
          margin="0 0 15px 0"
          type={alert.type}
          isVisible
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
      <Field
        isRequired
        label={t`Verification Code`}
        status={error.code ? 'error' : 'default'}
        desc={error.code}
        margin="0 0 20px 0">
        <TextInput
          size="large"
          placeholder={t`Enter 6-digit code`}
          maxLength={6}
          autoComplete="one-time-code"
          inputMode="numeric"
          {...register('code', { required: true })}
        />
      </Field>
      <Flex gap="15px" direction="column">
        <Button
          isFullWidth
          type="submit"
          size="large"
          variant="cta"
          isDisabled={isInvalid || isVerifying}
          isLoading={isVerifying}>
          {t`Verify`}
        </Button>
        <Flex justifyContent="space-between" alignItems="center">
          <Button
            type="button"
            size="small"
            variant="text"
            isDisabled={cooldown > 0 || isResending}
            onClick={onResend}>
            {cooldown > 0
              ? t`Resend code (${cooldown}s)`
              : t`Resend code`}
          </Button>
          <Button
            type="button"
            size="small"
            variant="text"
            onClick={onCancel}>
            {t`Cancel`}
          </Button>
        </Flex>
      </Flex>
    </form>
  )
}

Form.propTypes = {
  name: PropTypes.string.isRequired,
  token: PropTypes.string.isRequired,
  onVerified: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export { Form }
